import { useMutation, useQueryClient } from '@tanstack/react-query'
import React, { useState } from 'react'

import ActionButton from '@/components/misc/ActionButton'
import PlainActionButton from '@/components/misc/PlainActionButton'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Field, FieldLabel } from '@/components/ui/field'
import useCurrentCompanyOrFail from '@/hooks/useCurrentCompanyOrFail'
import { useShowGenericServerError } from '@/hooks/useShowGenericServerError'
import { QUERY_KEYS } from '@/lib/queryKeys'
import rqCertifiedAssessmentService from '@/lib/services/rqCertifiedAssessmentService'

interface Props {
  onConfirm: () => void
  onHide: () => void
}

const ConfirmNdaModal: React.FC<Props> = ({ onConfirm, onHide }) => {
  const currentCompany = useCurrentCompanyOrFail()
  const [hasAgreedToNda, setHasAgreedToNda] = useState(false)
  const createNdaMutation = useCreateNdaMutation()
  const confirmNdaMutation = useConfirmNdaMutation()
  const isCreatingNda = createNdaMutation.isPending
  const isConfirmingNda = confirmNdaMutation.isPending

  const ndaFileUrl = createNdaMutation.data?.ndaFileUrl

  return (
    <>
      <Dialog open onHide={onHide}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Non-Disclosure Agreement</DialogTitle>
          </DialogHeader>

          <DialogBody>
            <Field orientation="horizontal">
              <Checkbox
                checked={hasAgreedToNda}
                onCheckedChange={(checkedState) => {
                  setHasAgreedToNda(!!checkedState)
                }}
              />

              <FieldLabel>
                By ticking this box, {currentCompany.presentationName} confirms
                its intention to be legally bound by the provisions of the{' '}
                {ndaFileUrl ? (
                  <a href={ndaFileUrl} target="_blank" rel="noreferrer">
                    Non-Disclosure Agreement.
                  </a>
                ) : (
                  <PlainActionButton
                    onClick={() => createNdaMutation.mutate(currentCompany.id)}
                    isProcessing={isCreatingNda}
                    isProcessingText="Non-Disclosure Agreement"
                  >
                    Non-Disclosure Agreement.
                  </PlainActionButton>
                )}
              </FieldLabel>
            </Field>
          </DialogBody>

          <DialogFooter>
            <Button variant="outline-secondary" onClick={onHide}>
              Close
            </Button>

            <ActionButton
              disabled={!hasAgreedToNda || isConfirmingNda}
              isProcessing={isConfirmingNda}
              variant="primary"
              onClick={() => {
                confirmNdaMutation.mutate(currentCompany.id, {
                  onSuccess: () => onConfirm(),
                })
              }}
            >
              Start application
            </ActionButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function useCreateNdaMutation() {
  const showGenericServerError = useShowGenericServerError()

  return useMutation({
    mutationFn: async (companyId: number) =>
      rqCertifiedAssessmentService.createNdaPdf(companyId),
    onSuccess: (assessment) => {
      window.location.href = assessment.ndaFileUrl
    },
    onError: showGenericServerError,
  })
}

function useConfirmNdaMutation() {
  const queryClient = useQueryClient()
  const showGenericServerError = useShowGenericServerError()

  return useMutation({
    mutationFn: async (companyId: number) =>
      rqCertifiedAssessmentService.confirmNda(companyId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.rqCertifiedAssessment,
      })
    },
    onError: showGenericServerError,
  })
}

export default ConfirmNdaModal
